/**
 * group-service.ts — A minimal wrapper over the EUM group endpoints used by
 * user-management features (fetch a group, list its members, export members to
 * Excel).
 *
 * It mirrors the base-URL and request-body conventions of {@link UserService}:
 *   - When `apiBaseUrl` is provided, calls go directly to `{base}/eum_api/v1/…`
 *     and bodies are single-stringified.
 *   - Otherwise calls are proxied through `/api/EUMAPI/{Verb}?apiUrl=…` and
 *     write bodies are DOUBLE-stringified so the proxy can forward them intact.
 */
export class GroupService {
    constructor(private readonly apiBaseUrl = '') {}

    private get directBase(): string {
        return this.apiBaseUrl ? `${this.apiBaseUrl.replace(/\/$/, '')}/eum_api/v1/` : '';
    }

    private get usesProxy(): boolean {
        return !this.apiBaseUrl;
    }

    private headers(accept?: string): Record<string, string> {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json; charset=utf-8',
        };
        if (accept) headers['Accept'] = accept;
        const token = (window as any).EUMPortal?.bearerToken;
        if (token) headers['Authorization'] = token;
        return headers;
    }

    /** Build a request URL for the given relative API path and proxy verb. */
    private buildUrl(verb: 'Get' | 'Post' | 'Patch' | 'Delete' | 'PostObject', apiPath: string): string {
        return this.usesProxy
            ? `/api/EUMAPI/${verb}?apiUrl=${apiPath}`
            : `${this.directBase}${apiPath}`;
    }

    /**
     * Encode a write body. Through the proxy the payload must be double
     * stringified so `EUMAPIController` can forward the JSON string downstream.
     */
    private encodeBody(payload: unknown): string {
        return this.usesProxy
            ? JSON.stringify(JSON.stringify(payload))
            : JSON.stringify(payload);
    }

    /** Fetch a single group record. */
    async getGroup<T = Record<string, unknown>>(groupId: string): Promise<T> {
        const response = await fetch(this.buildUrl('Get', `groups/${encodeURIComponent(groupId)}`), {
            method: 'GET',
            headers: this.headers(),
        });
        if (!response.ok) throw new Error(`Error fetching group: ${response.status}`);
        return response.json();
    }

    /**
     * Resolve the EUM group(s) backing a SharePoint site-collection URL. Mirrors
     * the legacy directory's `getBySharePointUrl`: a search over the `group`
     * source filtered by a `search.ismatch` on `SharePointSiteCollectionURL`.
     */
    async getBySharePointUrl<T = Record<string, unknown>>(sharePointUrl: string): Promise<T> {
        const matchFilter = `search.ismatch('${sharePointUrl}', 'SharePointSiteCollectionURL', 'simple', 'all')`;
        const payload = {
            EUMSearchSources: ['group'],
            select: ['id', 'EUMDisplayName', 'AccessLevel'],
            searchOptions: {
                includeTotalCount: true,
                filter: `EUMSearchSource eq 'group' and (${matchFilter})`,
            },
        };
        const response = await fetch(this.buildUrl('PostObject', 'search/groups'), {
            method: 'POST',
            headers: this.headers(),
            body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error(`Error fetching groups by SharePoint URL: ${response.status}`);
        return response.json();
    }

    /**
     * List all groups available to the current user. Admins get the full group
     * index via `search/groups`; non-admins get only the groups they own or
     * manage via `users/ownedandmanagedgroups`. Mirrors the legacy directory's
     * `getAllGroups`.
     */
    async getAllGroups<T = Record<string, unknown>>(isAdmin: boolean): Promise<T> {
        if (isAdmin) {
            const payload = {
                EUMSearchSources: ['group'],
                select: ['id', 'EUMDisplayName', 'AccessLevel'],
                searchOptions: {
                    includeTotalCount: true,
                    filter: "EUMSearchSource eq 'group'",
                },
            };
            const response = await fetch(this.buildUrl('PostObject', 'search/groups'), {
                method: 'POST',
                headers: this.headers(),
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error(`Error fetching groups: ${response.status}`);
            return response.json();
        }

        const response = await fetch(this.buildUrl('Get', 'users/ownedandmanagedgroups'), {
            method: 'GET',
            headers: this.headers(),
        });
        if (!response.ok) throw new Error(`Error fetching managed groups: ${response.status}`);
        return response.json();
    }

    /** Fetch the members of a group. */
    async getMembers<T = Record<string, unknown>>(groupId: string): Promise<T> {
        const response = await fetch(this.buildUrl('Get', `groups/${encodeURIComponent(groupId)}/members`), {
            method: 'GET',
            headers: this.headers(),
        });
        if (!response.ok) throw new Error(`Error fetching group members: ${response.status}`);
        return response.json();
    }

    /**
     * Export users matching the supplied search payload to an Excel document,
     * returned as a binary Blob for download. The `searchText` is appended to
     * the export endpoint just as the legacy directory does.
     */
    async exportUsers(searchText: string, payload: Record<string, unknown>): Promise<Blob> {
        const query = encodeURIComponent(searchText || '');
        const url = this.usesProxy
            ? `/api/EUMAPI/PostObject?apiUrl=search/users%3FsearchText=${query}`
            : `${this.directBase}search/users?searchText=${query}`;

        const response = await fetch(url, {
            method: 'POST',
            headers: this.headers('application/octet-stream'),
            body: JSON.stringify(payload || {}),
        });
        if (!response.ok) throw new Error(`Error exporting users: ${response.status}`);
        return response.blob();
    }
}
